import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "axios";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./Home.css";

function Home() {
  const navigate = useNavigate();
  const [recentGames, setRecentGames] = useState([]);
  const [gameImages, setGameImages] = useState({});
  const [theme, setTheme] = useState("default");
  const [fallingItems, setFallingItems] = useState([]);
  const [popularGames, setPopularGames] = useState([]);
  localStorage.setItem("redirectAfterLogin", window.location.pathname);

  // Calculate Easter Sunday for a given year (using the Meeus/Jones/Butcher algorithm)
  const getEasterSunday = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  };

  // Calculate Thanksgiving (fourth Thursday of November) for a given year
  const getThanksgivingDate = (year) => {
    const novemberFirst = new Date(year, 10, 1);
    const dayOfWeek = novemberFirst.getDay();
    const firstThursday = 1 + ((4 - dayOfWeek + 7) % 7);
    const thanksgivingDay = firstThursday + 21;
    return new Date(year, 10, thanksgivingDay);
  };

  // Determine the current theme based on the date
  const determineTheme = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const day = now.getDate();

    const easterSunday = getEasterSunday(year);
    const easterMonth = easterSunday.getMonth() + 1;
    const easterDay = easterSunday.getDate();
    const easterStart = new Date(easterSunday);
    easterStart.setDate(easterDay - 7);
    const easterEnd = new Date(easterSunday);
    easterEnd.setDate(easterDay + 1);

    const thanksgiving = getThanksgivingDate(year);
    const thanksgivingMonth = thanksgiving.getMonth() + 1;
    const thanksgivingDay = thanksgiving.getDate();
    const thanksgivingStart = new Date(thanksgiving);
    thanksgivingStart.setDate(thanksgivingDay - 7);
    const thanksgivingEnd = new Date(thanksgiving);
    thanksgivingEnd.setDate(thanksgivingDay + 1);

    if (month === 10 && day >= 24 && day <= 31) {
      return "halloween";
    } else if ((month === 12 && day >= 12) || (month === 1 && day <= 1)) {
      return "newyear";
    } else if (now >= easterStart && now <= easterEnd) {
      return "easter";
    } else if (now >= thanksgivingStart && now <= thanksgivingEnd) {
      return "thanksgiving";
    } else {
      return "default";
    }
  };

  // Generate falling items for each theme
  const generateFallingItems = (theme) => {
    const items = [];
    const numberOfItems = 75;
    let contentOptions = [];

    switch (theme) {
      case "halloween":
        contentOptions = ["🎃", "👻", "🦇"];
        break;
      case "newyear":
        contentOptions = ["✻"];
        break;
      case "easter":
        contentOptions = ["🥚", "🐰"];
        break;
      case "thanksgiving":
        contentOptions = ["🍂", "🦃"];
        break;
      default:
        contentOptions = [];
    }

    for (let i = 0; i < numberOfItems; i++) {
      items.push({
        id: i,
        content: contentOptions[Math.floor(Math.random() * contentOptions.length)],
        style: {
          left: `${Math.random() * 100}vw`,
          animationDuration: `${Math.random() * 8 + 12}s`,
          animationDelay: `${Math.random() * -5}s`,
          fontSize: `${Math.random() * 20 + 10}px`,
        },
      });
    }

    setFallingItems(items);
  };

  // Steam AppID → custom header image
  const CUSTOM_GAME_IMAGES = {
    // Game has broken / missing Steam header
    "2623190":
      "https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/2623190/a7cee9165bb1bfc092c390c5cff215ce0e381dfc/header.jpg",

    // Hytale not on steam
    "4275070":
      "https://sintrix.net/hytale.jpg",

  };

  // Fetch game image
  const fetchGameImage = async (steamAppId) => {
    // 1️⃣ Custom override first
    const customImage = CUSTOM_GAME_IMAGES[String(steamAppId)];
    if (customImage) {
      const img = new Image();
      img.src = customImage;
      img.onload = () => {
        setGameImages((prev) => ({ ...prev, [steamAppId]: customImage }));
      };
      img.onerror = () => {
        setGameImages((prev) => ({ ...prev, [steamAppId]: "default-placeholder.jpg" }));
      };
      return;
    }

    // 2️⃣ Standard Fastly header
    let imageUrl = `https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/${steamAppId}/header.jpg`;
    const img = new Image();
    img.src = imageUrl;

    img.onload = () => {
      setGameImages((prev) => ({ ...prev, [steamAppId]: imageUrl }));
    };

    img.onerror = async () => {
      try {
        // 3️⃣ Fallback to backend appdetails
        const response = await axios.get(`/api/steam/appdetails/${steamAppId}`);
        if (response.data?.header_image) {
          imageUrl = response.data.header_image;
          const retryImg = new Image();
          retryImg.src = imageUrl;
          retryImg.onload = () => {
            setGameImages((prev) => ({ ...prev, [steamAppId]: imageUrl }));
          };
          retryImg.onerror = () => {
            setGameImages((prev) => ({ ...prev, [steamAppId]: "default-placeholder.jpg" }));
          };
        } else {
          setGameImages((prev) => ({ ...prev, [steamAppId]: "default-placeholder.jpg" }));
        }
      } catch (error) {
        console.error(`Error fetching image for appid ${steamAppId}:`, error);
        setGameImages((prev) => ({ ...prev, [steamAppId]: "default-placeholder.jpg" }));
      }
    };
  };

  const location = useLocation();

  useEffect(() => {
    try {
      const ins = document.querySelector('.adsbygoogle');
      if (ins && ins.innerHTML) ins.innerHTML = '';
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      console.error("AdSense error:", e);
    }
  }, [location.pathname]);

  useEffect(() => {
    const fetchPopularGames = async () => {
      try {
        const response = await axios.get("/api/auth/games/popular");
        setPopularGames(response.data);
        response.data.forEach((game) => {
          if (game.appid) {
            fetchGameImage(game.appid);
          }
        });
      } catch (error) {
        console.error("Error fetching popular games:", error);
      }
    };
    fetchPopularGames();
  }, []);

  useEffect(() => {
    const theme = determineTheme();
    setTheme(theme);
    generateFallingItems(theme);

    const fetchRecentGames = async () => {
      try {
        const response = await axios.get("/api/auth/games/recent");
        if (response.data && Array.isArray(response.data)) {
          setRecentGames(response.data);
          response.data.forEach((game) => {
            if (game.appid) {
              fetchGameImage(game.appid);
            }
          });
        } else {
          console.error("Fetched games are not an array:", response.data);
        }
      } catch (error) {
        console.error("Error fetching recent games:", error);
      }
    };

    fetchRecentGames();

    // Cursor trail effect logic
    const trailContainer = document.createElement("div");
    trailContainer.classList.add("cursor-trail-container");
    document.body.appendChild(trailContainer);

    const updateCursor = (e) => {
      const x = e.clientX;
      const y = e.clientY;

      // Create trail dot
      const trailDot = document.createElement("div");
      trailDot.classList.add("cursor-trail-dot");
      trailDot.style.left = `${x}px`;
      trailDot.style.top = `${y}px`;
      trailContainer.appendChild(trailDot);

      // Remove trail dot after animation
      setTimeout(() => {
        trailDot.remove();
      }, 1000);
    };

    document.addEventListener("mousemove", updateCursor);

    return () => {
      document.removeEventListener("mousemove", updateCursor);
      trailContainer.remove();
    };
  }, []);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 1200,
    slidesToShow: 5,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    responsive: [
      { breakpoint: 1200, settings: { slidesToShow: 4, slidesToScroll: 1 } },
      { breakpoint: 992, settings: { slidesToShow: 3, slidesToScroll: 1 } },
      { breakpoint: 768, settings: { slidesToShow: 1, slidesToScroll: 1 } },
    ],
  };

  return (
    <div className={`home-container theme-${theme}`}>
      <Helmet>
        <title>Home - Sintrix.net</title>
      </Helmet>
      <div className="falling-items-container">
        {fallingItems.map((item) => (
          <div key={item.id} className="falling-item" style={item.style}>
            {item.content}
          </div>
        ))}
      </div>
      <div className="home-content">
        <img
          src="/logo192.png"
          alt="HomeLogo"
          className="homelogo"
          onDoubleClick={() => navigate("/redpill")}
        />
        <h1 className="home-title">Welcome to Sintrix</h1>
        <p className="home-description">
          Welcome to the Sintrix website! We provide a user-friendly platform for our Cheat Engine tables.
          Playing a game and feeling like you're progressing too slowly? Want a little extra freedom in your gaming experience?
          We offer well-crafted, continually tested, and updated tables—and best of all, they are completely free!
        </p>
        <p>
          Feel free to browse our <strong>tables page</strong> and discover all the ready-made tables we offer.
          Just download, open, and use them to your heart's content!
        </p>
        <p>
          <strong>Would you like us to take a look at a game we don’t have yet? </strong>
          Register an account with us and gain access to useful features such as game requests, table bookmarking,
          profile customization, and messaging other members and staff.
          If you link your Discord, you’ll also be able to enable Discord notifications for game updates
          and gain access to our members-only section in the Discord server.
        </p>
        <p>
          <strong>Interested in our community?</strong> Feel free to check out our
          <a href="https://discord.gg/zjvdGQBrBc" target="_blank"> Discord server </a> to ask for support,
          chat with our staff and other users, or just hang out with our 10,000+ members!
        </p>
        <div className="home-buttons">
          <Link to="/requests" className="home-button">Cheat Requests</Link>
          <Link to="/games" className="home-button">Tables</Link>
        </div>
        <div className="ad-container">
          <ins
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-client="ca-pub-3980892685014254"
            data-ad-slot="9113462902"
            data-ad-format="auto"
            data-full-width-responsive="true"
          />
        </div>
        <hr className="separator-line" />
        <div className="recent-games-slider">
          <h2 className="recent-games-title">Most Recent Tables</h2>
          {Array.isArray(recentGames) && recentGames.length > 0 ? (
            <Slider {...sliderSettings}>
              {recentGames.map((game) => {
                const imageUrl = gameImages[game.appid] || "default-placeholder.jpg";
                return (
                  <div key={game._id} className="slider-item">
                    <Link to={`/game/${game.slug}`} className="game-card-link">
                      <img
                        src={imageUrl}
                        alt={`${game.name}`}
                        className="game-image"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "default-placeholder.jpg";
                        }}
                      />
                      <h3 className="game-title">{game.name}</h3>
                    </Link>
                  </div>
                );
              })}
            </Slider>
          ) : (
            <p className="no-games-message">No recent games available.</p>
          )}
        </div>
        <hr className="separator-line recent-games" />
        <div className="popular-games-slider">
          <h2 className="popular-games-title">Most Popular Tables</h2>
          {popularGames.length > 0 ? (
            <Slider {...sliderSettings}>
              {popularGames.map((game) => {
                const imageUrl = gameImages[game.appid] || "default-placeholder.jpg";
                return (
                  <div key={game._id} className="slider-item">
                    <Link to={`/game/${game.slug}`} className="game-card-link">
                      <img
                        src={imageUrl}
                        alt={game.name}
                        className="game-image"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "default-placeholder.jpg";
                        }}
                      />
                      <h3 className="game-title">{game.name}</h3>
                    </Link>
                  </div>
                );
              })}
            </Slider>
          ) : (
            <p className="no-games-message">No popular games available.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Home;